import { ProductDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef, NewValueParams, RowClassParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, Select, message } from 'antd';
import { differenceInHours } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { ProductState, productCategories, productPackings, productStates, productStorages } from 'src/constants';

import { arrayValueFormatter, formatDateForAgCell, formatNumberForAgCell, loadGridState } from 'src/utils/ag-grid-util';
import { errorObjectToString, formatNumber, sleep } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { updateProduct } from 'src/utils/firebase/firebase-short-cut';
import { preprocessProductData, productValueSanitizer, productValueValidator } from 'src/utils/product-util';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellInventory from 'src/atomic-components/atoms/AgCell/AgCellInventory';
import AgCellProductSwitchEditor from 'src/atomic-components/atoms/AgCell/AgCellProductSwitchEditor';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import CreateNewProductModal from 'src/atomic-components/organisms/Modals/CreateNewProductModal';
import {
  ProductCategoryCellRenderer,
  ProductPackingCellRenderer,
  ProductStateCellRenderer,
  ProductStoragesCellRenderer,
} from 'src/components/Common/ProductCellRenderer/ProductCellRenderer';
import ProductImg from 'src/components/Common/ProductImg/ProductImg';

import classes from './ProductTable.module.scss';

export type RowData = ProductDoc & { productSupplierName?: string };

////////////////////////////////////////////////////////////////////////////////////
// 상품 편집 기능

const productValueSetter = (params: any) => {
  const field = params.colDef.field;
  if (!field) return false;
  try {
    // 1. sanitize
    const value = productValueSanitizer(field, params.newValue);
    // 2. validate
    productValueValidator(field, value);
    // 3. change cell value
    params.data[field] = value;
    return true;
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    message.error(msg);
    return false;
  }
};

const productOnCellValueChanged = async (params: NewValueParams<RowData>) => {
  const field = params.colDef.field;
  if (params.oldValue === params.newValue || !field) return;

  try {
    const value = (params.data as any)[field];
    // 4. update db
    const data = preprocessProductData(params.data, field, value);
    await updateProduct(params.data._id, data);
    message.success('변경되었습니다');
    // 5. re-focus after data update
    if (params?.node && params?.node?.rowIndex !== null) {
      await sleep(0);
      params.api.setFocusedCell(params?.node?.rowIndex, field);
    }
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    message.error(msg);
    // 5. rollback
    if (params.node) {
      (params.data as any)[field] = params.oldValue;
      params.api.refreshCells({ rowNodes: [params.node], force: true });
    }
  }
};
////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////
// ag-grid settings

const defaultColDef: ColDef<RowData> = {
  sortable: true,
  resizable: true,
  filter: true,
  floatingFilter: true,
};

const now = new Date();
const TABLE_ID = 'productTable';

const getRowStyle = (params: RowClassParams<RowData>): any => {
  const { costUpdatedAt } = params.node.data ?? {};
  if (costUpdatedAt) {
    const diff = differenceInHours(now, new Date(costUpdatedAt));
    // 변경된지 18시간 이내면 노란색으로 표시한다.
    if (diff < 18) {
      return { backgroundColor: 'var(--yellow200)' };
    }
  }
  return { backgroundColor: 'white' };
};

////////////////////////////////////////////////////////////////////////////////////

interface ProductTableProps {
  rowData: RowData[];
  selectedStates: ProductState[];
  setSelectedStates: (states: ProductState[]) => void;
}

const ProductTable: FC<ProductTableProps> = ({ rowData, selectedStates, setSelectedStates }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<RowData> | null>(null);

  const columnDefs: (ColDef<RowData> | ColGroupDef<RowData>)[] = useMemo(
    () => [
      { headerName: 'productId', field: '_id', minWidth: 200, hide: true },
      {
        headerName: '업데이트',
        field: '_timeUpdate',
        hide: true,
        width: 108,
        valueFormatter: (params: any) => (params.value ? formatDateForAgCell('yy-MM-dd')(params) : 'New'),
        cellStyle: (params: any) => {
          if (!params.value) {
            return { color: 'var(--blue400)', fontWeight: 'bold' };
          }
        },
      },
      {
        headerName: '매입가 변경',
        field: 'costUpdatedAt',
        sortable: true,
        sort: 'desc',
        width: 140,
        valueGetter: (params: any) =>
          params.data.costUpdatedAt ? formatDate(params.data.costUpdatedAt, 'yyyy-MM-dd') : null,
      },
      { headerName: '코드', field: 'productId', width: 98, filter: 'agTextColumnFilter' },
      { headerName: '이미지', field: 'thumbnail', width: 96, cellRenderer: ProductImg },
      { headerName: '판매금액', field: 'salesAmount', width: 98, valueFormatter: formatNumberForAgCell },
      {
        headerName: '품명',
        field: 'fullName',
        filter: 'agTextColumnFilter',
        width: 240,
      },
      {
        headerName: '*품명1',
        field: 'name',
        filter: 'agTextColumnFilter',
        width: 120,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*구분',
        field: 'categories',
        width: 108,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellRenderer: ProductCategoryCellRenderer,
        cellEditorParams: {
          values: Object.keys(productCategories),
          cellRenderer: ProductCategoryCellRenderer,
        },
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        filterParams: {
          valueFormatter: (params: any) => productCategories[params.value],
        },
      },
      {
        headerName: '*과/면세',
        field: 'taxFree',
        width: 108,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        valueFormatter: ({ value }) => (value ? '면세' : '과세'),
        cellEditorParams: {
          values: [true, false],
        },
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '매입가',
        field: 'cost',
        width: 120,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => formatNumber(value),
      },
      {
        headerName: '*판매가',
        field: 'price',
        width: 120,
        editable: true,
        filter: 'agNumberColumnFilter',
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        valueFormatter: ({ value }) => formatNumber(value),
      },
      {
        headerName: '마진율',
        width: 110,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          const { cost, price } = (params.data ?? {}) as RowData;
          if (!cost || !price) return null;
          return ((price - cost) / price) * 100;
        },
        valueFormatter: (params) => {
          const margin = params.value as number;
          if (!margin) return '';
          return `${formatNumber(margin, 2)}%`;
        },
      },
      {
        headerName: '단위가격',
        field: 'unitPrice',
        width: 120,
        valueGetter: (params) => {
          const { unitPrice } = (params.data ?? {}) as RowData;
          if (!unitPrice) return '';
          return unitPrice.price;
        },
        valueFormatter: (params) => {
          const price = params.value as number;
          const { unitPrice } = (params.data ?? {}) as RowData;
          if (!unitPrice) return '';
          return formatNumber(price) + ` / ${unitPrice.amount}${unitPrice.unit}`;
        },
      },
      {
        headerName: '판매상태',
        field: 'state',
        width: 120,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellRenderer: ProductStateCellRenderer,
        cellEditorParams: {
          values: Object.keys(productStates),
          cellRenderer: ProductStateCellRenderer,
        },
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '거래처',
        field: 'productSupplierName',
        width: 120,
      },
      {
        headerName: '재고 변경',
        field: 'inventoryName',
        width: 200,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => {
          return {
            inventoryId: params.data.inventoryId,
            product: params.data,
            field: 'inventoryName',
          };
        },
      },
      {
        headerName: '재고 상세',
        field: 'inventoryName',
        width: 200,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => {
          return {
            inventoryId: params.data.inventoryId,
            field: 'inventoryName',
          };
        },
      },
      {
        headerName: '발주 품명',
        field: 'purchaseConfig.purchaseProductName',
        width: 200,
        filter: false,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => {
          return {
            inventoryId: params.data.inventoryId,
            field: 'purchaseConfig.purchaseProductName',
          };
        },
      },
      {
        headerName: '*숨김',
        field: 'hidden',
        width: 96,
        cellRenderer: (params: any) => {
          const data = params.data as RowData;
          const value = params.value ?? false;
          return <AgCellProductSwitchEditor productId={data._id} value={value} field='hidden' />;
        },
      },
      {
        headerName: '*가격알림',
        field: 'dailyNotification',
        width: 108,
        cellRenderer: (params: any) => {
          const data = params.data as RowData;
          const value = params.value ?? false;
          return <AgCellProductSwitchEditor productId={data._id} value={value} field='dailyNotification' />;
        },
      },
      {
        headerName: '*보관',
        field: 'storage',
        width: 96,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellRenderer: ProductStoragesCellRenderer,
        cellEditorParams: {
          values: Object.keys(productStorages),
          cellRenderer: ProductStoragesCellRenderer,
        },
        filterParams: {
          valueFormatter: (params: any) => productStorages[params.value],
        },
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*패킹',
        field: 'packing',
        width: 96,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellRenderer: ProductPackingCellRenderer,
        cellEditorParams: {
          values: Object.keys(productPackings),
          cellRenderer: ProductPackingCellRenderer,
        },
        filterParams: {
          valueFormatter: (params: any) => productPackings[params.value],
        },
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*단위',
        field: 'measure',
        width: 120,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        valueGetter: (params) => {
          const measure = params.data?.measure;
          return Array.isArray(measure) ? measure.map((m) => `${m.amount}${m.unit}`).join('*') : '-';
        },
      },
      {
        headerName: '*브랜드',
        field: 'brand',
        width: 96,
        editable: true,
        filter: 'agTextColumnFilter',
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*특징',
        field: 'features',
        width: 96,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        valueFormatter: arrayValueFormatter,
      },
      {
        headerName: '*원산지',
        field: 'origin',
        width: 96,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*대표태그',
        field: 'mainTag',
        width: 108,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*태그',
        field: 'tags',
        width: 96,
        editable: true,
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        valueFormatter: arrayValueFormatter,
      },
      {
        headerName: '*배지',
        field: 'badge',
        width: 96,
        editable: true,
        filter: 'agTextColumnFilter',
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
        valueFormatter: arrayValueFormatter,
      },
      {
        headerName: '*이미지설명',
        field: 'imageDescription',
        width: 116,
        editable: true,
        filter: 'agTextColumnFilter',
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '*패킹정보',
        field: 'packingInfo',
        width: 116,
        editable: true,
        filter: 'agTextColumnFilter',
        valueSetter: productValueSetter,
        onCellValueChanged: productOnCellValueChanged,
      },
      {
        headerName: '액션',
        hide: true,
        width: 120,
        cellRenderer: DownloadRawDataCell,
      },
    ],
    []
  );

  const hideColumnsForPurchaseSetting = useCallback(() => {
    gridRef?.columnApi?.setColumnsVisible(
      [
        'name',
        'dailyNotification',
        'imageDescription',
        'thumbnail',
        'memo',
        'imageUrl',
        'categories',
        'taxFree',
        'stuck',
        'hidden',
        'brand',
        'packing',
        'storage',
        'origin',
        'features',
        'mainTag',
        'tags',
        'cost',
        'badge',
      ],
      false
    );
  }, [gridRef?.columnApi]);

  const autoSizeAll = useCallback(() => {
    gridRef?.columnApi?.autoSizeAllColumns(true);
    loadGridState(gridRef, TABLE_ID);
  }, [gridRef]);

  return (
    <div className={`ag-theme-alpine ${classes.tableContainer}`}>
      <div className='tableButtons'>
        <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
      </div>
      <div className='gridHeader' style={{ display: 'flex', justifyContent: 'space-between' }}>
        <CreateNewProductModal />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 8 }}>
          <span>상품 수: {formatNumber(rowData.length)}</span>
        </div>
        <Select
          options={Object.entries(productStates).map(([key, value]) => ({ label: value, value: key }))}
          mode='multiple'
          style={{ minWidth: 120 }}
          value={selectedStates}
          onChange={(value) => {
            setSelectedStates(value as ProductState[]);
          }}
        />
        <Button onClick={hideColumnsForPurchaseSetting} style={{ margin: '0 8px' }}>
          컬럼 간소화
        </Button>
      </div>
      <AgGridReact
        ref={setGridRef}
        rowData={rowData}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => params.data._id}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        gridOptions={{ rowHeight: 63 }}
        onGridReady={autoSizeAll}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        alwaysShowHorizontalScroll={true}
        animateRows={true}
        getRowStyle={getRowStyle}
      />
    </div>
  );
};

export default ProductTable;
